import React, { Component, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { stringify } from 'query-string';
import { find } from 'lodash';
import i18n from 'cityads-v3/i18n';
import { ParkedDomainType } from 'cityads-v3/hooks/domainParking'

import Button from '@mui/material/Button'
import CopyTextField from 'cityads-v3/components/CopyTextField/CopyTextField';
import Icon from '@mui/material/Icon'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import ParkedDomains from 'cityads-v3/components/TextField/ParkedDomains'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField';

import withHooks from 'cityads-v3/decorators/withHooks';
import { useCouponInfoSender } from 'cityads-v3/hooks/amplitude';

import {
  getShortLink,
} from 'cityads-v3/actions/shortLink';
import {
  getChannels
} from 'cityads-v3/actions/channels';

import ExportPopup from './ExportPopup';
import classNames from './ExportPopup.scss';

const PROMO_TYPE_PROMOCODE = 6;

@withHooks(props => {
  const { id, open } = props
  const userChannelsById = useSelector(state => state.channels.channelsById);
  const channels = useSelector(state => state.offers.couponLinksByCouponId[id] || []);
  const sendCouponInfo = useCouponInfoSender(id);

  const channelsById = useMemo(() => channels.reduce((result, channel) => {
    result[channel.id] = {
      ...channel,
      name: userChannelsById[channel.id]?.name
    }
    return result
  }, {}), [channels, userChannelsById])

  useEffect(() => {
    if (open) {
      sendCouponInfo('d-v1-e35', 'coupons_url_view');
    }
  }, [open, sendCouponInfo]);

  return {
    ...props,
    dispatch: useDispatch(),
    sendCouponInfo,
    channels,
    channelsById,
    couponShortLinkByCouponId: useSelector(state => state.shortLink.shortLinks),
    channelsLoading: useSelector(state => state.channels.isLoading),
    channelsLoaded: useSelector(state => state.channels.isLoaded),
    userChannelsById: useSelector(state => state.channels.channelsById),
    domains: useSelector(state => state.entities.parkedDomains?.data?.parkedDomain) ?? [],
  };
})
@withTranslation(['Offers'], { i18n, wait: !!window })
export default class CouponExportPopup extends Component {

  static propTypes = {
    open: PropTypes.bool,
    offerId: PropTypes.number,
    offerName: PropTypes.string,
    offerIsDeeplinkEnabled: PropTypes.bool,
    channels: PropTypes.array,
    promoActionType: PropTypes.number,
    couponShortLinkByCouponId: PropTypes.object,
    channelsLoading: PropTypes.bool,
    channelsLoaded: PropTypes.bool,
    userChannelsById: PropTypes.object,
    sendCouponInfo: PropTypes.func.isRequired,
    channelsById: PropTypes.object,
    domains: PropTypes.array,
  }

  state = {
    isShortLink: false,
    selectedChannel: +this.props.channels[0]?.id || 0,
    codeIframe: this.props.channels[0]?.iframe || '',
    codeLink: this.props.channels[0]?.link || '',
    selectedCodeType: 'Link',
    url: '',
    subaccounts: [''],
    domainId: ''
  }

  componentDidMount() {
    const { dispatch, channelsLoaded, channelsLoading } = this.props;

    if (!channelsLoaded && !channelsLoading) {
      dispatch(getChannels());
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { dispatch, channels, channelsLoaded, channelsLoading, open } = this.props;

    if (
      channels.length &&
      (!this.state.selectedChannel || channels !== prevProps.channels)
    ) {
      this.onChannelChange(channels[0].id);
    }

    if (!channelsLoaded && !channelsLoading) {
      dispatch(getChannels());
    }

    if (prevProps.open && !open) {
      this.onChannelChange()
      this.setState({
        url: '',
        subaccounts: ['']
      })
    }
  }

  copyCode = () => {
    this.copy.select();
    document.execCommand('copy');
    this.onCopyCode()
  }

  onCopyCode = () => {
    this.props.sendCouponInfo('d-v1-e36', 'coupons_url_copy')
  }

  onChannelChange = (id) => {
    const channel = this.props.channelsById[id];

    this.setState({
      selectedChannel: channel ? +channel.id : 0,
      codeIframe: channel?.iframe || '',
      codeLink: channel?.link || '',
      isShortLink: false
    });
  }

  onChangeCodeType = (evt, value) => {
    this.setState({
      selectedCodeType: value,
      isShortLink: false
    });
  }

  onSubaccountChange = (subaccounts) => {
    this.setState({
      subaccounts,
      isShortLink: false
    });
  }

  makeCode(isShortLink = this.state.isShortLink) {
    const { domainId } = this.state
    const { domains, open } = this.props

    if (!open) { return '' }

    if (isShortLink) {
      const shortLink = this.props.couponShortLinkByCouponId[`couponShortLink_${this.props.id}`];
      if (shortLink) {
        return shortLink;
      }
    }

    const subaccounts = this.state.subaccounts.reduce((acc, sa, index) => ({
      ...acc,
      ['sa' + (index ? index + 1 : '')]: sa || null
    }), {});

    let link = ''
    try {
      link = new URL(
        this.state['code' + this.state.selectedCodeType] + stringify({
          url: this.state.url || null,
          ...subaccounts
        }, { skipNull: true })
      )
    } catch (e) { }

    if (domainId) {
      const selectedDomain = find(domains, { id: domainId })
      if (selectedDomain) {
        link.hostname = selectedDomain.domain
      }
    }

    return link.toString()
  }

  toggleShortLink = () => {
    if (this.state.isShortLink === false) {
      this.props.dispatch(getShortLink(this.makeCode(false), `couponShortLink_${this.props.id}`));
    }

    this.setState({
      isShortLink: !this.state.isShortLink
    });
  }

  handleChangeDomain = (evt) => {
    this.setState({
      domainId: +evt.target.value,
      isShortLink: false
    })
  }

  render() {
    const {
      t,
      id,
      offerId,
      offerName,
      offerIsDeeplinkEnabled,
      channels,
      domains,
      promoActionType,
      couponShortLinkByCouponId,
      channelsLoaded,
      channelsLoading,
      userChannelsById,
      channelsById,
      sendCouponInfo,
      ...rest
    } = this.props;

    const title = (
      <div className={classNames['title--coupon']}>
        <div>
          <a href={`/publisher/offer/${offerId}`} target="_blank" rel="noopener noreferrer">
            {offerName}
            <Icon
              color="primary"
              style={{
                fontSize: '12px',
                verticalAlign: 'super'
              }}
            >open_in_new</Icon>
          </a>
        </div>
        <div className={classNames.subtitle}>{t('exports.couponExportTitle')}</div>
      </div>
    );

    const codeType = promoActionType === PROMO_TYPE_PROMOCODE && (
      <FormControl variant="standard" component="fieldset" required fullWidth className={classNames.codeType}>
        <InputLabel
          component="legend"
          shrink
          classes={{ asterisk: classNames.required }}
        >
          {t('exports.selectCodeType')}
        </InputLabel>
        <RadioGroup
          className={classNames.codeType__select}
          row={true}
          name="codeType"
          value={this.state.selectedCodeType}
          onChange={this.onChangeCodeType}
        >
          <FormControlLabel value="Link" control={<Radio />} label={t('exports.codeTypeLink')} />
          <FormControlLabel value="Iframe" control={<Radio />} label={t('exports.codeTypeIframe')} />
        </RadioGroup>
      </FormControl>
    );

    return (
      <ExportPopup
        {...rest}
        title={title}
        channels={channels.map(item => ({...item, name: channelsById[item.id]?.name}))}
        selectedChannel={this.state.selectedChannel}
        onChannelChange={this.onChannelChange}
        onSubaccountChange={this.onSubaccountChange}
        subaccounts={this.state.subaccounts}
        showMore={false}
      >
        <ParkedDomains
          domainType={ParkedDomainType.links}
          onChange={this.handleChangeDomain}
          value={this.state.domainId}
        />

        {offerIsDeeplinkEnabled ?
          (
            <TextField
              placeholder=""
              label={t('exports.url')}
              fullWidth={true}
              value={this.state.url}
              onChange={evt => this.setState({ url: evt.target.value, isShortLink: false })}
              InputLabelProps={{ shrink: true }}
            />
          )
          :
          null
        }

        {codeType}

        <CopyTextField
          label={t('exports.couponLink')}
          value={this.makeCode() || ''}
          multiline={true}
          fullWidth={true}
          onClick={this.onCopyCode.bind(this)}
          inputProps={{ref: copy => this.copy = copy}}
        />

        <div className={classNames.copyButtons}>
          <Button
            variant="contained"
            onClick={this.copyCode}
            color="primary"
          >{t('exports.copy')}</Button>
          <Button
            variant="text"
            onClick={this.toggleShortLink}
          >{this.state.isShortLink ? t('exports.getFullLink') : t('exports.getShortLink')}</Button>
        </div>
      </ExportPopup>
    );
  }

};
